import { TemplateResult, html, CSSResultGroup } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement, property, state } from 'lit/decorators.js';
import {
  screenSizeMap,
  ScreenSizeController,
} from '../../controllers/screen-size-controller';
import componentStyles from './sic-anchor-nav.css.lit';

/**
 * The Anchor Nav component
 *
 * @element sic-anchor-nav
 * @since 1.0.0
 * @slot anchors - slot on ul containing li with links links
 * @slot aside-title - title above nav links (not shown on mobile)
 * @slot aside-cta - button link below nav links
 * @slot content - content containing Ids to be linked to in nav links
 *
 * Lightdom styles in sic-anchor-nav.lightdom.css included in /shared.css
 */
@customElement('sic-anchor-nav')
export class SicAnchorNav extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  screens = new ScreenSizeController(this);
  listItems: NodeListOf<HTMLLIElement>;

  /**
   * The breakpoint at which the secondary menu will be visible.
   *
   * This is done as a variable to ensure flexibility for future usages.
   */
  @property({ type: String, attribute: 'breakpoint' })
  visibleBreakpoint = 'lg';

  /**
   * The aria-label on the <nav> element that communicates the purpose of the section.
   */
  @property({ type: String, attribute: 'nav-label' })
  navLabel = 'Quick Links';

  /**
   * Must be a hash string (ex. `#section-1`)
   */
  @state() activeHash: string | null;

  /**
   * If the users browser is set to 'prefers-reduced-motion' will prevent scrolling and cleanly jump to selected section.
   */
  @state()
  preventScroll = false;

  @state()
  isMobile = false;

  @state()
  pauseNavScroll = false;

  @state()
  scrollOffset = 60;

  firstUpdated() {
    this._getHashFromUrl();
    this._addClickListener();
    this._scrollSpy();
    this._setReduceMotion();
  }

  updated() {
    const links: NodeListOf<HTMLElement> = this.querySelectorAll(
      '[slot="anchors"] li'
    );

    links.forEach(el => {
      // Clear the previous state on all <li> & child <a> elements.
      el.removeAttribute('active');
      const a = el.querySelector<HTMLAnchorElement>('a');
      a?.removeAttribute('aria-current');

      // Apply the new state to the current  <li> & child <a> element.
      if (a?.hash === this.activeHash) {
        el.setAttribute('active', 'true');
        a.setAttribute('aria-current', 'location');
      }
    });
  }

  disconnectedCallback() {
    this._removeClickListener();
    window.onscroll = null;
  }

  render(): TemplateResult {
    this.isMobile =
      window.innerWidth < (screenSizeMap.get(this.visibleBreakpoint) as number);

    return html`
      <div class="wrapper ${this.isMobile ? 'is-mobile' : null}">
        <div class="content">
          ${this.isMobile ? this.ctaTemplate() : null}
          <slot name="content"></slot>
        </div>
        <aside>
          <div class="anchor-nav">
            <slot name="aside-title"></slot>
            <nav aria-label="${this.navLabel}">
              <slot name="anchors"></slot>
            </nav>
            ${!this.isMobile ? this.ctaTemplate() : null}
          </div>
        </aside>
      </div>
    `;
  }

  ctaTemplate() {
    return html`<slot name="aside-cta"></slot>`;
  }

  _getHashFromUrl = (): void => {
    if (window.location.hash) {
      this.activeHash = window.location.hash;
      this.requestUpdate();
    }
  };

  _addClickListener = (): void => {
    this.querySelectorAll('[slot="anchors"]').forEach(el =>
      el.addEventListener('click', this._handleClick)
    );
  };

  _removeClickListener = (): void => {
    this.querySelectorAll('[slot="anchors"]').forEach(el =>
      el.removeEventListener('click', this._handleClick)
    );
  };

  _scrollToAndFocus = (target: HTMLElement): void => {
    target.setAttribute('tabindex', '-1');
    target.addEventListener('blur', () => target.removeAttribute('tabindex'));

    target.focus({ preventScroll: true });
    const targetPosition = target.getBoundingClientRect().top;
    const offsetPosition =
      targetPosition + window.pageYOffset - this.scrollOffset;
    window.scrollTo({
      top: offsetPosition,
      behavior: this.preventScroll ? 'auto' : 'smooth',
    });
  };

  _handleClick = (ev: Event): void => {
    const targetAnchor = ev.target as HTMLAnchorElement;
    const targetId = targetAnchor?.hash.substring(1);
    const targetEl = document.getElementById(targetId);
    const header = document.querySelector('sic-main-header');

    if (!targetEl) return;
    ev.preventDefault();
    this.pauseNavScroll = true;
    header?.setAttribute('haltScrollShow', '');
    this._scrollToAndFocus(targetEl);
    setTimeout(() => {
      this.pauseNavScroll = false;
      header?.removeAttribute('haltScrollShow');
    }, 750);
  };

  _scrollSpy() {
    const sections = this.getElementsByTagName('section');
    const scrollObserver = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.activeHash = `#${entry.target.id}`;
            window.history.replaceState(null, '', `#${entry.target.id}`);
          }
        }
      },
      {
        rootMargin: '-50% 0px',
      }
    );

    for (let i = 0; i < sections.length; i++) {
      scrollObserver.observe(sections[i]);
    }
  }
  /**
   * If user has prefers reduced motion set, prevents scrolling behavior and jumps the page to the link.
   */
  _setReduceMotion() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.preventScroll = true;
    }
  }
}
